import { useMemo, useRef, useState } from 'react';

import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { useSortable } from '@dnd-kit/react/sortable';
import { IconButton } from 'design-system';
import { GripVertical, Move, Settings, X } from 'lucide-react';

import { TABNAME_MAX_LENGTH } from '@/config/name';
import { canEditWidgetInDashboard } from '@/features/dashboard/components/apps';
import { DashboardNameInput } from '@/features/dashboard/components/DashboardNameInput';
import { createNameSchema } from '@/features/dashboard/lib/validateName';
import { useRemoveWidgetContext } from '@/features/dashboard/state/RemoveWidgetContext';

import { MoveMenu } from './MoveMenu';

import type { ItemProps } from '../types';

/**
 * Item component representing a single tab within an app.
 * Supports drag-and-drop reordering and inline name editing.
 */
export const Item = ({
  index,
  item,
  column,
  onRemoveItem,
  container,
  isSingleItem,
  onEdit,
  currentDashboardName,
  currentTabId,
  onTabCreated,
  onRename
}: ItemProps) => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<HTMLButtonElement | null>(null);
  const [moveItemMenuAnchor, setMoveItemMenuAnchor] = useState<HTMLElement | null>(null);

  useSortable({
    id: item.id,
    element: elementRef,
    index,
    handle: handleRef,
    type: isSingleItem ? 'singleItem' : 'item',
    accept: ['item', 'singleItem'],
    group: column,
    modifiers: [RestrictToElement.configure({ element: container.current })]
  });

  const { openRemoveModal } = useRemoveWidgetContext();

  const nameErrors = useMemo(() => {
    const res = createNameSchema('Name', TABNAME_MAX_LENGTH).safeParse(item.name);
    if (res.success) return undefined;
    return res.error;
  }, [item.name]);

  const isEditable = canEditWidgetInDashboard(item);

  return (
    <div
      ref={elementRef}
      aria-disabled='true'
      className='relative flex h-12 min-w-[180px] items-center gap-2 overflow-visible whitespace-nowrap border border-dashed border-gx-highlight text-gx-text outline-0 transition-[outline] data-[tab-drop-over=true]:outline-2 data-[tab-drop-over=true]:outline-gx-highlight'
      role='tab'
    >
      <button
        ref={handleRef}
        aria-label='Drag to reorder tab'
        className='tab-drag-handle z-10 flex h-[46px] w-4 shrink-0 cursor-grab items-center justify-center border-0 bg-gx-response p-0 text-gx-text'
        title='Drag to reorder tab'
        type='button'
      >
        <GripVertical className='size-3.5' />
      </button>

      <div className='max-w-[300px] [&_input]:h-[46px] [&_input]:text-gx-text-disabled focus-within:[&_input]:text-gx-text hover:[&_input]:text-gx-text'>
        <DashboardNameInput
          ariaLabel='App name'
          error={nameErrors?.issues}
          maxLength={TABNAME_MAX_LENGTH}
          value={item.name}
          onChange={onRename}
        />
      </div>
      <div className='edit-buttons__container ml-auto flex gap-0.5'>
        <IconButton
          className='h-[46px] rounded-none'
          label='Move to ...'
          variant='ghost'
          onClick={e => {
            e.stopPropagation();
            setMoveItemMenuAnchor(e.currentTarget);
          }}
        >
          <Move aria-hidden='true' />
        </IconButton>
        {isEditable && (
          <IconButton
            className='h-[46px] rounded-none'
            label='Configuration settings'
            variant='ghost'
            onClick={e => {
              e.stopPropagation();
              onEdit(item.id);
            }}
          >
            <Settings aria-hidden='true' />
          </IconButton>
        )}
        <IconButton
          className='h-[46px] rounded-none'
          label='Remove tab'
          variant='ghost'
          onClick={e => {
            e.stopPropagation();
            openRemoveModal(() => {
              onRemoveItem(item.id);
            });
          }}
        >
          <X aria-hidden='true' />
        </IconButton>
        <MoveMenu
          anchorEl={moveItemMenuAnchor}
          appId={column}
          currentDashboardName={currentDashboardName}
          currentTabId={currentTabId}
          item={item}
          open={Boolean(moveItemMenuAnchor)}
          variant='item'
          onClose={() => {
            setMoveItemMenuAnchor(null);
          }}
          onRemoveFromCurrent={() => {
            onRemoveItem(item.id);
          }}
          onTabCreated={onTabCreated}
        />
      </div>
    </div>
  );
};

export default Item;
